import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import {
  getOfflineReaderSyncEventName,
  getOfflineReaderSyncQueueCount,
} from "@/lib/offline-reader-sync";
import {
  getPendingReaderAnnotationSyncCount,
  getReaderAnnotationSyncEventName,
} from "@/hooks/use-reader-annotations";

type OfflineSyncTone = "offline" | "syncing" | "synced";

type OfflineSyncStatus = {
  isOnline: boolean;
  pendingCount: number;
  tone: OfflineSyncTone;
  label: string;
  detail: string;
  visible: boolean;
  signature: string;
};

const getPendingCount = (userId?: string | null) =>
  getOfflineReaderSyncQueueCount() + getPendingReaderAnnotationSyncCount(userId);

export function useOfflineSyncStatus(): OfflineSyncStatus {
  const { user } = useAuth();
  const [isOnline, setIsOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [pendingCount, setPendingCount] = useState(() => getPendingCount(user?.id));
  const [showSyncedFeedback, setShowSyncedFeedback] = useState(false);

  useEffect(() => {
    setPendingCount(getPendingCount(user?.id));
  }, [user?.id]);

  useEffect(() => {
    const syncEvents = [getOfflineReaderSyncEventName(), getReaderAnnotationSyncEventName()];
    let syncedFeedbackTimeout: ReturnType<typeof window.setTimeout> | null = null;

    const refreshState = () => {
      const nextOnline = typeof navigator === "undefined" ? true : navigator.onLine;
      const nextPendingCount = getPendingCount(user?.id);

      setIsOnline(nextOnline);
      setPendingCount((current) => {
        if (current > 0 && nextPendingCount === 0 && nextOnline) {
          setShowSyncedFeedback(true);
          if (syncedFeedbackTimeout) {
            window.clearTimeout(syncedFeedbackTimeout);
          }
          syncedFeedbackTimeout = window.setTimeout(() => setShowSyncedFeedback(false), 2400);
        }
        return nextPendingCount;
      });
    };

    refreshState();

    window.addEventListener("online", refreshState);
    window.addEventListener("offline", refreshState);
    window.addEventListener("storage", refreshState);
    syncEvents.forEach((eventName) => window.addEventListener(eventName, refreshState));

    return () => {
      window.removeEventListener("online", refreshState);
      window.removeEventListener("offline", refreshState);
      window.removeEventListener("storage", refreshState);
      syncEvents.forEach((eventName) => window.removeEventListener(eventName, refreshState));
      if (syncedFeedbackTimeout) {
        window.clearTimeout(syncedFeedbackTimeout);
      }
    };
  }, [user?.id]);

  return useMemo(() => {
    if (!isOnline) {
      return {
        isOnline,
        pendingCount,
        tone: "offline" as const,
        label: pendingCount > 0 ? `Offline - ${pendingCount} changes saved` : "Offline mode",
        detail:
          pendingCount > 0
            ? "Your reading changes will sync when the connection returns."
            : "Saved PDFs still open from your offline library.",
        visible: pendingCount > 0,
        signature: `offline:${pendingCount}`,
      };
    }

    if (pendingCount > 0) {
      return {
        isOnline,
        pendingCount,
        tone: "syncing" as const,
        label: `Syncing ${pendingCount} change${pendingCount === 1 ? "" : "s"}`,
        detail: "Highlights, progress, and study sessions are being uploaded.",
        visible: true,
        signature: `syncing:${pendingCount}`,
      };
    }

    if (showSyncedFeedback) {
      return {
        isOnline,
        pendingCount,
        tone: "synced" as const,
        label: "All reading changes synced",
        detail: "Offline work is safely stored in your account.",
        visible: true,
        signature: "synced:visible",
      };
    }

    return {
      isOnline,
      pendingCount,
      tone: "synced" as const,
      label: "All reading changes synced",
      detail: "Offline work is safely stored in your account.",
      visible: false,
      signature: "synced:hidden",
    };
  }, [isOnline, pendingCount, showSyncedFeedback]);
}
